import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { useSessionGuard } from '@/hooks/useSessionGuard';
import { SessionKickedDialog } from '@/components/session/SessionKickedDialog';
import { safeError } from '@/lib/safeError';

interface AuthContextType {
  isAuthenticated: boolean;
  authLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  user: User | null;
  session: Session | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Clear Supabase auth tokens from localStorage when the server can't be reached */
function clearLocalAuth() {
  try {
    // Supabase stores tokens under a key like sb-<ref>-auth-token
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        localStorage.removeItem(key);
      }
    }
  } catch { /* localStorage unavailable (incognito etc.) */ }
}

/**
 * Backstop for the admin verification queue.
 *
 * signup-notification (which stamps user_account_levels.verification_requested_at
 * and emails the admin + welcome) is invoked client-side from the email signup
 * form, but the Google-OAuth path is a full redirect and never gets the chance —
 * so OAuth signups were created unverified yet invisible to the VerificationQueue
 * (which filters on verification_requested_at). A failed client-side call on the
 * email path could strand a user the same way.
 *
 * On a fresh sign-in we check whether this user is still unverified AND has no
 * verification_requested_at; if so we invoke signup-notification once. This
 * covers OAuth signups and self-heals an earlier missed notification on the
 * user's next login. The DB check makes it idempotent (once stamped it won't
 * fire again); the in-memory set avoids re-querying on repeat auth events within
 * a session. Verified users and team-invited members (who already have a
 * verification_requested_at or are verified) are untouched.
 */
const signupNotificationChecked = new Set<string>();

async function ensureSignupNotification(session: Session | null) {
  const user = session?.user;
  if (!user || signupNotificationChecked.has(user.id)) return;
  signupNotificationChecked.add(user.id);

  try {
    const { data: level, error } = await supabase
      .from('user_account_levels')
      .select('is_verified, verification_requested_at')
      .eq('user_id', user.id)
      .maybeSingle();

    // Only act on a confirmed unverified user with no request already on record.
    if (error || !level || level.is_verified || level.verification_requested_at) return;

    const meta = (user.user_metadata ?? {}) as Record<string, string>;
    const fullName =
      meta.full_name ||
      meta.name ||
      [meta.first_name, meta.last_name].filter(Boolean).join(' ') ||
      'New Appraiser';

    await supabase.functions.invoke('signup-notification', {
      body: { userId: user.id, fullName, email: user.email ?? '', userType: 'appraiser' },
    });
  } catch (err) {
    console.warn('[AuthContext] signup-notification backstop failed:', safeError(err));
  }
}

function SessionGuardProvider({ session, children }: { session: Session | null; children: ReactNode }) {
  const { kicked, kickedByDeviceLabel, staySignedIn, signOut } = useSessionGuard(session);

  return (
    <>
      {children}
      <SessionKickedDialog
        open={kicked}
        deviceLabel={kickedByDeviceLabel}
        onStaySignedIn={staySignedIn}
        onSignOut={signOut}
      />
    </>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    // getSession first to hydrate immediately, then subscribe for future changes
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsAuthenticated(!!session);
      setAuthLoading(false);
    }).catch((err) => {
      console.error('[AuthContext] Failed to get session:', safeError(err));
      // Clear potentially corrupt tokens so the user isn't stuck
      clearLocalAuth();
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsAuthenticated(!!session);
        setAuthLoading(false);

        // If token refresh failed, Supabase may emit SIGNED_OUT or
        // deliver a null session on TOKEN_REFRESHED — force clean state
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('[AuthContext] Token refresh returned no session — clearing auth');
          clearLocalAuth();
        }

        // Fresh sign-in (password or OAuth redirect return): ensure the user has
        // a verification request on record so they reach the admin queue. Deferred
        // to avoid calling supabase APIs synchronously inside this callback.
        if (event === 'SIGNED_IN') {
          setTimeout(() => { void ensureSignupNotification(session); }, 0);
        }
      }
    );

    // Proactively refresh the session when the tab regains focus.
    // When a tab is dormant, the auto-refresh timer may not fire,
    // leaving an expired token that causes 401s on the next API call.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session: freshSession } }) => {
          if (freshSession) {
            // Check if the token is close to expiry (within 2 minutes)
            const expiresAt = freshSession.expires_at;
            if (expiresAt && expiresAt - Math.floor(Date.now() / 1000) < 120) {
              supabase.auth.refreshSession();
            }
          }
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    return { success: true };
  };

  const signup = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl },
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      // Server-side sign-out failed (500, network error, etc.)
      // Clear local tokens anyway so the user isn't stuck in a zombie session.
      // The server-side token will expire on its own.
      console.warn('[AuthContext] signOut failed, clearing local session:', error.message);
      clearLocalAuth();
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, authLoading, login, signup, logout, user, session }}>
      <SessionGuardProvider session={session}>
        {children}
      </SessionGuardProvider>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Safe variant that returns null when called outside AuthProvider (e.g. public member sites). */
export function useOptionalAuth() {
  return useContext(AuthContext) ?? null;
}
